import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ShoppingBag, Menu, X, User, LogOut } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";

const links = [
  { label: "Treatments", to: "/#services" },
  { label: "Pricing", to: "/pricing" },
  { label: "Skin Analysis", to: "/skin-analysis" },
  { label: "Results", to: "/#results" },
  { label: "Shop", to: "/shop" },
];

export default function Header() {
  const { count, setOpen } = useCart();
  const { user, logout } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [mobile, setMobile] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const accountPath = user?.role === "admin" ? "/admin" : "/dashboard";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNav = (to) => {
    setMobile(false);
    if (to.startsWith("/#")) {
      const id = to.slice(2);
      if (location.pathname !== "/") {
        navigate("/");
        setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 300);
      } else {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      navigate(to);
    }
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled ? "bg-white/70 backdrop-blur-xl border-b border-white/40" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <div className="hidden md:flex items-center gap-8 flex-1">
          {links.slice(0, 2).map((l) => (
            <button
              key={l.label}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              onClick={() => handleNav(l.to)}
              className="text-sm font-semibold text-[#1A1A1A] hover:text-[#B0872A] transition-colors"
            >
              {l.label}
            </button>
          ))}
        </div>

        <Link
          to="/"
          data-testid="logo-link"
          className="font-display text-2xl md:text-3xl tracking-tight text-gold font-semibold flex-shrink-0"
        >
          Women&nbsp;Heaven
        </Link>

        <div className="hidden md:flex items-center gap-8 flex-1 justify-end">
          {links.slice(2).map((l) => (
            <button
              key={l.label}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              onClick={() => handleNav(l.to)}
              className="text-sm font-semibold text-[#1A1A1A] hover:text-[#B0872A] transition-colors"
            >
              {l.label}
            </button>
          ))}
          <button
            data-testid="cart-button"
            onClick={() => setOpen(true)}
            className="relative text-[#1A1A1A] hover:text-[#B0872A] transition-colors"
          >
            <ShoppingBag className="w-5 h-5" strokeWidth={1.5} />
            {count > 0 && (
              <span className="absolute -top-2 -right-2 bg-[#2B2340] text-white text-[10px] w-4 h-4 rounded-full flex items-center justify-center">
                {count}
              </span>
            )}
          </button>
          {user ? (
            <>
              <Link to={accountPath} data-testid="nav-account" className="relative text-[#1A1A1A] hover:text-[#B0872A] transition-colors">
                <User className="w-5 h-5" strokeWidth={1.5} />
              </Link>
              <button data-testid="nav-logout" onClick={logout} className="text-[#1A1A1A] hover:text-[#B0872A] transition-colors">
                <LogOut className="w-5 h-5" strokeWidth={1.5} />
              </button>
            </>
          ) : (
            <Link to="/login" data-testid="nav-login" className="text-sm text-[#1A1A1A] hover:text-[#B0872A] transition-colors">
              Login
            </Link>
          )}
        </div>

        <div className="flex md:hidden items-center gap-5">
          <button data-testid="cart-button-mobile" onClick={() => setOpen(true)} className="relative">
            <ShoppingBag className="w-5 h-5" strokeWidth={1.5} />
            {count > 0 && (
              <span className="absolute -top-2 -right-2 bg-[#2B2340] text-white text-[10px] w-4 h-4 rounded-full flex items-center justify-center">
                {count}
              </span>
            )}
          </button>
          <button data-testid="mobile-menu-toggle" onClick={() => setMobile((m) => !m)}>
            {mobile ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {mobile && (
        <div className="md:hidden bg-white/95 backdrop-blur-xl border-t border-[#DAD5E2] px-6 py-6 flex flex-col gap-5">
          {links.map((l) => (
            <button
              key={l.label}
              data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              onClick={() => handleNav(l.to)}
              className="text-left text-lg font-display"
            >
              {l.label}
            </button>
          ))}
          <button onClick={() => handleNav("/consultation")} className="btn-primary justify-center mt-2">
            Free Consultation
          </button>
          {user ? (
            <>
              <button data-testid="mobile-nav-account" onClick={() => handleNav(accountPath)} className="text-left text-lg font-display">
                {user.role === "admin" ? "Admin Console" : "My Dashboard"}
              </button>
              <button data-testid="mobile-nav-logout" onClick={() => { logout(); setMobile(false); }} className="text-left text-lg font-display text-[#8C3B3B]">
                Logout
              </button>
            </>
          ) : (
            <button data-testid="mobile-nav-login" onClick={() => handleNav("/login")} className="text-left text-lg font-display">
              Login / Register
            </button>
          )}
        </div>
      )}
    </header>
  );
}
